import { useState, useEffect } from "react";
import { WifiOff, RefreshCw, Check } from "lucide-react";
import { subscribeToOfflineState } from "@/lib/offlineSync";

export default function OfflineBanner() {
  const [isOnline, setIsOnline] = useState(true);
  const [syncStatus, setSyncStatus] = useState<"idle" | "syncing" | "error">("idle");
  const [pendingCount, setPendingCount] = useState(0);
  const [showSynced, setShowSynced] = useState(false);

  useEffect(() => {
    let syncedTimer: ReturnType<typeof setTimeout>;
    const unsub = subscribeToOfflineState((state) => {
      const wasOffline = !isOnline;
      setIsOnline(state.isOnline);
      setSyncStatus(state.syncStatus);
      setPendingCount(state.pendingCount);

      if (wasOffline && state.isOnline && state.pendingCount === 0 && state.syncStatus === "idle") {
        setShowSynced(true);
        syncedTimer = setTimeout(() => setShowSynced(false), 3000);
      }
    });
    return () => {
      unsub();
      clearTimeout(syncedTimer);
    };
  }, [isOnline]);

  if (isOnline && !showSynced && syncStatus !== "syncing" && pendingCount === 0) {
    return null;
  }

  return (
    <div className="fixed top-0 left-0 right-0 z-[100] animate-in slide-in-from-top duration-300">
      {!isOnline && (
        <div className="bg-amber-900/95 border-b border-amber-700/50 px-4 py-2 flex items-center justify-center gap-2 text-sm backdrop-blur-sm">
          <WifiOff className="w-4 h-4 text-primary" />
          <span className="text-amber-100 font-medium">
            You're offline
          </span>
          {pendingCount > 0 && (
            <span className="text-amber-300/80 text-xs ml-1">
              ({pendingCount} change{pendingCount !== 1 ? "s" : ""} queued)
            </span>
          )}
        </div>
      )}

      {isOnline && syncStatus === "syncing" && (
        <div className="bg-sky-900/95 border-b border-sky-700/50 px-4 py-2 flex items-center justify-center gap-2 text-sm backdrop-blur-sm">
          <RefreshCw className="w-4 h-4 text-sky-400 animate-spin" />
          <span className="text-sky-100 font-medium">
            Syncing {pendingCount} change{pendingCount !== 1 ? "s" : ""}...
          </span>
        </div>
      )}

      {showSynced && isOnline && syncStatus === "idle" && pendingCount === 0 && (
        <div className="bg-green-900/95 border-b border-green-700/50 px-4 py-2 flex items-center justify-center gap-2 text-sm backdrop-blur-sm">
          <Check className="w-4 h-4 text-green-400" />
          <span className="text-green-100 font-medium">
            All changes synced
          </span>
        </div>
      )}
    </div>
  );
}
